/**
 * Preferințele per utilizator — oglinda registrului din backend
 * (`backend/src/modules/user-preference/user-preference.constants.ts`).
 * Modifică-le împreună: o cheie adăugată doar aici e respinsă de server.
 */
export const USER_PREFERENCE_DEFINITIONS = {
  /** După salvarea unei oferte: true = revino la pagina anterioară, false = rămâi în formular. */
  'offers.redirectAfterSave': { type: 'boolean', default: true },
  /** După crearea unui client: true = revino la pagina anterioară, false = rămâi în formular. */
  'clients.redirectAfterCreate': { type: 'boolean', default: true },
  /** După editarea unui client: true = revino la pagina anterioară, false = rămâi în formular. */
  'clients.redirectAfterEdit': { type: 'boolean', default: true },
} as const;

export type UserPreferenceKey = keyof typeof USER_PREFERENCE_DEFINITIONS;

type PreferenceTypeMap = { boolean: boolean; string: string; number: number };

/** Tipul valorii unei chei, derivat din `type` în registru. */
export type UserPreferenceValue<K extends UserPreferenceKey> = PreferenceTypeMap[(typeof USER_PREFERENCE_DEFINITIONS)[K]['type']];

/** Toate preferințele, rezolvate (valoarea salvată sau implicitul). */
export type UserPreferences = { [K in UserPreferenceKey]: UserPreferenceValue<K> };

export const USER_PREFERENCE_KEYS = Object.keys(USER_PREFERENCE_DEFINITIONS) as UserPreferenceKey[];

/** Ce întoarce serverul pentru o cheie (`myPreferences` / `updateMyPreferences`). */
export interface UserPreferenceEntry {
  key: string;
  value: unknown;
  isDefault: boolean;
}

/** Un element din `updateMyPreferences`. `value: null` = revino la implicit. */
export interface UpdateUserPreferenceInput {
  key: UserPreferenceKey;
  value: unknown;
}

export function isUserPreferenceKey(key: string): key is UserPreferenceKey {
  return Object.prototype.hasOwnProperty.call(USER_PREFERENCE_DEFINITIONS, key);
}

export function defaultUserPreferences(): UserPreferences {
  const out = {} as Record<UserPreferenceKey, unknown>;
  for (const key of USER_PREFERENCE_KEYS) out[key] = USER_PREFERENCE_DEFINITIONS[key].default;
  return out as UserPreferences;
}

/** Transformă lista de la server în record rezolvat; cheile necunoscute sau cu tip greșit cad pe implicit. */
export function resolveUserPreferences(entries: UserPreferenceEntry[] | null | undefined): UserPreferences {
  const out = defaultUserPreferences() as Record<UserPreferenceKey, unknown>;
  for (const entry of entries ?? []) {
    if (!isUserPreferenceKey(entry.key)) continue;
    if (typeof entry.value !== USER_PREFERENCE_DEFINITIONS[entry.key].type) continue;
    out[entry.key] = entry.value;
  }
  return out as UserPreferences;
}
